import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { useToast } from '@/hooks/use-toast';
import { Tables, TablesInsert, TeamSideType } from '@/integrations/supabase/types';
import { formatUnknownError } from '@/lib/error-format';
import { queryKeys } from '@/lib/query-keys';

export type OrgPerson = Tables<'org_people'>;
export type OrgPersonInsert = Omit<TablesInsert<'org_people'>, 'user_id'>;

export interface OrgPersonFormData {
  name: string;
  email?: string;
  role_title: string;
  side_type: 'agency_internal' | 'agency_partner';
  department?: string;
  notes?: string;
  is_active?: boolean;
}

export function useOrgPeople() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const orgPeopleQuery = useQuery({
    queryKey: queryKeys.organization.people(organization?.id),
    queryFn: async () => {
      // Filter by organization_id to satisfy RLS policy
      const { data, error } = await supabase
        .from('org_people')
        .select('*')
        .eq('organization_id', organization!.id)
        .order('name');

      if (error) throw error;
      return data as OrgPerson[];
    },
    enabled: !!user?.id && !!organization?.id,
  });

  const activeOrgPeople = orgPeopleQuery.data?.filter(p => p.is_active) || [];

  const addOrgPerson = useMutation({
    mutationFn: async (formData: OrgPersonFormData) => {
      if (!user?.id) throw new Error('Not authenticated');
      if (!organization?.id) throw new Error('No organization');

      const { data, error } = await supabase
        .from('org_people')
        .insert({
          user_id: user.id,
          organization_id: organization.id,
          name: formData.name,
          email: formData.email || null,
          role_title: formData.role_title,
          side_type: formData.side_type,
          department: formData.department || null,
          notes: formData.notes || null,
          is_active: formData.is_active ?? true,
        })
        .select()
        .single();

      if (error) throw error;
      return data as OrgPerson;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.people(organization?.id) });
      toast({ title: 'Team member added' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to add team member',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });

  const updateOrgPerson = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<OrgPerson> & { id: string }) => {
      const { data, error } = await supabase
        .from('org_people')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as OrgPerson;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.people(organization?.id) });
      toast({ title: 'Team member updated' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update team member',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });

  const deleteOrgPerson = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('org_people')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.people(organization?.id) });
      toast({ title: 'Team member removed' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to remove team member',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });

  const toggleOrgPersonActive = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { data, error } = await supabase
        .from('org_people')
        .update({ is_active })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as OrgPerson;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.people(organization?.id) });
      toast({ title: data.is_active ? 'Team member activated' : 'Team member deactivated' });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update team member',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });

  const updateClickUpMapping = useMutation({
    mutationFn: async ({ id, clickup_user_id, clickup_username }: {
      id: string;
      clickup_user_id: string | null;
      clickup_username: string | null;
    }) => {
      const { data, error } = await supabase
        .from('org_people')
        .update({ clickup_user_id, clickup_username })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as OrgPerson;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization.people(organization?.id) });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update ClickUp mapping',
        description: formatUnknownError(error),
        variant: 'destructive',
      });
    },
  });

  return {
    orgPeople: orgPeopleQuery.data || [],
    activeOrgPeople,
    isLoading: orgPeopleQuery.isLoading,
    addOrgPerson,
    updateOrgPerson,
    deleteOrgPerson,
    toggleOrgPersonActive,
    updateClickUpMapping,
  };
}
